import * as moment from "moment";
import { DatePipe } from "@angular/common";
import { NewsfeedComment } from "./newsfeed-comment";
import { NewsfeedLike } from "./newsfeed-like";
import { NewsfeedKeyword } from "./newsfeed-keyword";
import { Keyword } from "./keyword";
import { CustomParticipant } from "./custom-participant";
import { Participant } from "./participant";
import { NewsfeedTranslation } from "./newsfeed-translation";
import { NewsfeedCarousel } from "./newsfeed-carousel";

import { Translatable } from "./translatable.interface";

import { environment } from "../../environments/environment";
import { EmbedVideoPipe } from "../pipes/string/embed-video";
import { isExternalLink } from "../helpers/url";

export class Newsfeed implements Translatable {
    id: number;
    created_at: string;
    updated_at: string;
    user_id: number;
    event_id: number;
    participant_id: number;
    custom_participant_id: number;
    status: number;
    message: string;
    image: string;
    image_url: string;
    file: string;
    file_url: string;
    filename: string;
    video: string;
    video_url: string;
    link: string;
    link_name: string;
    pinned: boolean = false;
    promoted: boolean = false;
    edited: boolean = false;

    likes: NewsfeedLike[] = [];
    keywords: NewsfeedKeyword[] = [];
    keywordsUnique: NewsfeedKeyword[] = [];
    comments: NewsfeedComment[] = [];
    editComment: NewsfeedComment = new NewsfeedComment;
    custom_participant: CustomParticipant = new CustomParticipant;
    participant: Participant = new Participant;

    longTextVisible: boolean = false;
    commentsVisible: boolean = false;

    carousels: NewsfeedCarousel[] = [];

    refuse_reason: string;
    video_embed_link: string;
    display_video: boolean = true;
    linkInNewTab: boolean = true;

    translations: NewsfeedTranslation[] = [];

    number_of_likes: number = 0;

    options: {
        oneColumn: boolean
    } = {
            oneColumn: false
        };

    shareCredential: boolean = false;

    /**
     * constructor
     *
     * @return void
     */
    public constructor(init?: Newsfeed) {
        if (!!init) {
            Object.assign(this, init);
            this.translations = this.translations.map(t => new NewsfeedTranslation(t));

            // initiate comments
            this.comments = this.comments.map(comment => new NewsfeedComment(comment));

            // initiate likes
            this.likes = this.likes.map(like => new NewsfeedLike(like));

            // initiate keywords
            this.keywords = this.keywords.map(keyword => new NewsfeedKeyword(keyword));
            this.initKeywordsUnique();

            this.initVideoEmbedLink();

            if (init.custom_participant) {
                this.custom_participant = new CustomParticipant(init.custom_participant);
            }

            // open links with same host in same window/tab, others in new tab
            if (this.link) {
                this.linkInNewTab = isExternalLink(this.link);
            }

            if (this.created_at != this.updated_at) {
                this.edited = true;
            }

        }
    }

    /**
     * parse and set the actual last video link in (text + link) string
     */
    public initVideoEmbedLink() {

        if (this.video) {

            this.video_embed_link = 'https://player.vimeo.com/video/' + this.video.replace('/videos/', '');

            return this.video_embed_link;
        }

        this.video_embed_link = new EmbedVideoPipe().transform(this.message + ' ' + this.link);

        return this.video_embed_link;
    }

    /**
     * initiate the array with unique keywords
     */
    public initKeywordsUnique() {
        this.keywordsUnique = [];

        this.keywords.forEach(keyword => {
            if (!this.keywordsUnique.filter(item => {
                return item.keyword.name == keyword.keyword.name;
            }).length) {
                this.keywordsUnique.push(keyword);
            }
        });
    }

    /**
     * return real participant if possible
     * @return Participant || false
     */
    public getRealParticipant() {
        let participant = null;

        // selected participant by organizer
        if (this.custom_participant) {
            if (this.custom_participant.participant_id) {
                participant = this.custom_participant.participant;
            }
        }

        // normal participant
        else if (this.participant_id) {
            participant = this.participant;
        }

        return participant || false;
    }

    public hasValue() {
        return this.id || this.message || this.video || this.file || this.image || this.keywords.length > 0 || this.link;
    }

    /**
     * get translated attributes
     *
     * @param locale
     *
     * @return {VotingTranslation}
     */
    public translateOrNew(locale: string) {
        let currentTranslation = this.translations.filter(translation => translation.locale === locale)[0];

        if (!currentTranslation) {
            currentTranslation = new NewsfeedTranslation;
            currentTranslation.locale = locale;

            this.translations.push(currentTranslation);
        }

        return currentTranslation;
    }

    /**
     * translate
     *
     * @param locale
     * @param defaultLocale
     * @return {VotingTranslation}
     */
    public translate(locale: string, defaultLocale?: string) {
        let translation = this.translations.filter(translation => { return translation.locale === locale; })[0] ||
            this.translations.filter(translation => { return translation.locale === defaultLocale; })[0] ||
            new NewsfeedTranslation({
                message: this.message
            });
        if (translation) {
            return translation;
        } else {
            return this;
        }
    }
}
